import imageCompression from 'browser-image-compression';

export interface CompressImageParams {
  maxSizeMB?: number;
  maxWidth?: number;
}

export async function compressImage(file: any, params?: CompressImageParams) {
  // 300KB is enough when it is spent on resolution instead of on a big ceiling: 1280px on the long
  // side is 1.5x the pixels of the previous 1024px for 1.5x the bytes, so the photo keeps more of the
  // tree's detail - and `alwaysKeepResolution` makes the library pay for that ceiling with quality
  // only, never by shrinking the resolution.
  const defaultParams = {
    maxSizeMB: 0.3,
    maxWidthOrHeight: 1280,
    alwaysKeepResolution: true,
    useWebWorker: false,
  };

  try {
    const compressed = await imageCompression(file, {
      ...defaultParams,
      ...(params ? params : {})
    });

    return toFile(compressed, file);
  } catch (error) {
    console.warn('Image compression failed, using original file', { error });
    return toFile(file, file);
  }
}

/**
 * browser-image-compression does not always return a File.
 *
 * On the OffscreenCanvas path - the one WebKit started taking once Safari
 * shipped convertToBlob - it returns the Blob produced by the canvas and simply
 * assigns a `name` property to it. FormData ignores that property because the
 * value is not a File instance, and uploads the part as "blob", without any
 * extension, which the API rejects. Wrapping it back into a real File keeps the
 * filename all the way to the server.
 */
function toFile(compressed: any, originalFile: any): File {
  if (compressed instanceof File) {
    return compressed;
  }

  const filename = compressed?.name || originalFile?.name || 'photo.jpg';
  const type = compressed?.type || originalFile?.type || 'image/jpeg';

  try {
    return new File([compressed], filename, { type, lastModified: Date.now() });
  } catch (error) {
    // File is constructible everywhere we support, but a failure here must not
    // lose the picture: the blob still carries its name for entriesToFormData.
    console.error('could not rebuild the compressed image as a File', { error });
    return compressed;
  }
}
